import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { ApiError } from '../api/client';
import { downloadUrl, getRun, regenerateRun, type RunDetail, type RunStatus } from '../api/runs';

/**
 * `/runs/:id` detail view.
 *
 * The view is deliberately read-mostly:
 *
 *   - Three read endpoints drive the page: `GET /runs/:id`, and two 302
 *     "navigate-the-browser" download endpoints (CSV, bundle) plus a per-row
 *     PDF. Downloads are never fetched from JS — `<a href>` hands off to the
 *     browser so the signed-URL redirect + cookie boundaries stay sane
 *     (see `api/runs.ts` for the full reasoning).
 *   - One mutation: `POST /runs/:id/regenerate`. It spawns a *new* run,
 *     returns its id, and leaves the current run's row untouched. We show a
 *     confirmation via `window.confirm` — the action is destructive-adjacent
 *     (it enqueues work), not data-destructive, and a native dialog is a
 *     proportionate gate for v1. Swap to a custom modal when the design
 *     system lands.
 *
 * Polling: while the run's status is one of the non-terminal states
 * (`PENDING`, `PARSING`, `RENDERING`), we refetch every 2s so the operator
 * sees progress without clicking refresh. Once the server reports `COMPLETE`
 * or `FAILED`, polling stops — those are terminal.
 *
 * Error shapes handled explicitly:
 *   - 404 → bespoke "run not found" panel with a link back to the list.
 *   - All other ApiErrors → surface `err.detail` verbatim; the server's
 *     problem+json is the canonical error message for the UI.
 */

/** Non-terminal statuses — the run is still moving, so we keep polling. */
const IN_FLIGHT_STATUSES: ReadonlySet<RunStatus> = new Set(['PENDING', 'PARSING', 'RENDERING']);

/** 2s poll cadence matches the list view (T-052) for consistency. */
const POLL_INTERVAL_MS = 2000;

/**
 * Format a YYYY-MM month string to "April 2026" for display. Matches the PDF
 * cover page's formatting so operators see the same label across surfaces.
 *
 * `null` is a legitimate state — the worker has not yet parsed the first row.
 * We render an em-dash rather than "null" or an empty string.
 */
function formatMonth(month: string | null): string {
  if (month === null) return '—';
  // `YYYY-MM` → construct as UTC to avoid local-timezone off-by-one at
  // month boundaries (e.g. 2026-04-01T00:00 in UTC-5 is still "March" locally).
  const [yearStr, monthStr] = month.split('-');
  const year = Number(yearStr);
  const monthIdx = Number(monthStr) - 1;
  if (!Number.isFinite(year) || !Number.isFinite(monthIdx)) return month;
  const date = new Date(Date.UTC(year, monthIdx, 1));
  return new Intl.DateTimeFormat('en-GB', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(date);
}

const GBP_FORMATTER = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' });

function formatPence(pence: number): string {
  return GBP_FORMATTER.format(pence / 100);
}

/** Match the pipeline's rounding: `Math.round(seconds / 60)`. */
function formatMinutes(seconds: number): string {
  return String(Math.round(seconds / 60));
}

function formatUploadedAt(iso: string): string {
  return new Date(iso).toLocaleString('en-GB');
}

/**
 * CSS classes for anchor-styled buttons. The anchor carries real href so a
 * middle-click / right-click "Open in new tab" does the right thing; visual
 * disabling is the `pointer-events-none + opacity` pair, and the semantic
 * disabling is `aria-disabled + tabIndex={-1}`.
 */
const BUTTON_BASE =
  'inline-flex items-center px-3 py-2 rounded bg-brand-teal text-white text-sm font-medium hover:bg-brand-teal/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-teal/60';
const BUTTON_DISABLED = 'pointer-events-none opacity-50';

function statusLabel(status: RunStatus): string {
  // Title-case for display. The backend ships SHOUTING_SNAKE; we do not want
  // that in the UI header.
  return status.charAt(0) + status.slice(1).toLowerCase();
}

interface DownloadAnchorProps {
  href: string;
  label: string;
  disabled: boolean;
  testId?: string;
}

function DownloadAnchor({ href, label, disabled, testId }: DownloadAnchorProps): JSX.Element {
  const className = `${BUTTON_BASE} ${disabled ? BUTTON_DISABLED : ''}`.trim();
  // When disabled, we still render an `<a>` (rather than a `<button disabled>`)
  // so the DOM shape stays identical across states — easier to reason about,
  // easier to test. `tabIndex={-1}` keeps a disabled control out of the tab
  // order; `aria-disabled` tells AT clients what is going on.
  const extraProps = disabled
    ? ({ 'aria-disabled': true, tabIndex: -1 } as const)
    : ({ 'aria-disabled': false } as const);
  return (
    <a href={href} className={className} data-testid={testId} {...extraProps}>
      {label}
    </a>
  );
}

export function RunDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();

  if (id === undefined || id.length === 0) {
    // Defensive — the router should never give us an empty id for this path,
    // but bailing here is cheaper than an ambiguous "fetch /runs/undefined".
    return (
      <section aria-label="Run detail">
        <p>Missing run id.</p>
      </section>
    );
  }

  return <RunDetailView id={id} />;
}

interface RunDetailViewProps {
  id: string;
}

function RunDetailView({ id }: RunDetailViewProps): JSX.Element {
  const queryClient = useQueryClient();
  const [regenerateMessage, setRegenerateMessage] = useState<string | null>(null);

  const query = useQuery<RunDetail, ApiError>({
    queryKey: ['run', id],
    queryFn: () => getRun(id),
    refetchInterval: (q) => {
      const data = q.state.data;
      if (data && IN_FLIGHT_STATUSES.has(data.status)) return POLL_INTERVAL_MS;
      return false;
    },
  });

  const regenerate = useMutation({
    mutationFn: () => regenerateRun(id),
    onSuccess: (created) => {
      setRegenerateMessage(`Regenerating — new run created (${created.runId}).`);
      // Invalidate the current run (status may flip via audit side-effects)
      // and the list so a sibling runs page picks up the new row immediately.
      void queryClient.invalidateQueries({ queryKey: ['run', id] });
      void queryClient.invalidateQueries({ queryKey: ['runs'] });
    },
  });

  if (query.isPending) {
    return (
      <section aria-label="Run detail">
        <div role="status" aria-live="polite" aria-busy="true">
          Loading run…
        </div>
      </section>
    );
  }

  if (query.isError) {
    const err = query.error;
    if (err instanceof ApiError && err.status === 404) {
      return (
        <section aria-label="Run detail">
          <h1 className="text-xl font-semibold mb-2">Run not found</h1>
          <p className="mb-4">No run exists with id {id}.</p>
          <Link to="/runs" className="text-brand-teal underline">
            Back to runs
          </Link>
        </section>
      );
    }
    const message = err instanceof ApiError ? err.detail : 'Failed to load run.';
    return (
      <section aria-label="Run detail">
        <h1 className="text-xl font-semibold mb-2">Error</h1>
        <p role="alert">{message}</p>
        <p className="mt-4">
          <Link to="/runs" className="text-brand-teal underline">
            Back to runs
          </Link>
        </p>
      </section>
    );
  }

  const run = query.data;
  const bundleDisabled = run.status !== 'COMPLETE';

  const handleRegenerate = (): void => {
    // Native confirm is intentional for v1 — see the module docstring for the
    // rationale. A custom modal lands with the shared design system later.
    const confirmed = window.confirm(
      'Regenerate this run? A new run will be created using the same source CSV.',
    );
    if (!confirmed) return;
    setRegenerateMessage(null);
    regenerate.mutate();
  };

  return (
    <section aria-label="Run detail" className="flex flex-col gap-6">
      <header className="flex flex-col gap-2">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-semibold">{formatMonth(run.month)}</h1>
            <p className="text-sm text-slate-600">
              <span data-testid="run-original-filename">{run.originalFilename}</span>
              <span aria-hidden="true"> · </span>
              <span data-testid="run-uploaded-at">Uploaded {formatUploadedAt(run.uploadedAt)}</span>
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span
              className="inline-flex items-center px-2 py-1 rounded bg-slate-200 text-slate-800 text-sm"
              data-testid="run-status"
            >
              {statusLabel(run.status)}
            </span>
            <button
              type="button"
              onClick={handleRegenerate}
              disabled={regenerate.isPending}
              className={`${BUTTON_BASE} ${regenerate.isPending ? BUTTON_DISABLED : ''}`.trim()}
            >
              {regenerate.isPending ? 'Regenerating…' : 'Regenerate'}
            </button>
          </div>
        </div>

        {run.errorMessage !== undefined && run.errorMessage !== null && run.errorMessage !== '' ? (
          <p role="alert" className="text-sm text-red-700">
            {run.errorMessage}
          </p>
        ) : null}

        <dl className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm mt-2">
          <div>
            <dt className="text-slate-500">Customers</dt>
            <dd data-testid="run-customer-count">{run.customerCount ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-slate-500">PDFs ready</dt>
            <dd data-testid="run-pdf-count">{run.pdfCount ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-slate-500">Rows</dt>
            <dd data-testid="run-row-count">{run.rowCount ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-slate-500">Status</dt>
            <dd>{statusLabel(run.status)}</dd>
          </div>
        </dl>
      </header>

      <div className="flex gap-3 flex-wrap">
        <DownloadAnchor
          href={downloadUrl(run.id, 'csv')}
          label="Download CSV"
          disabled={false}
          testId="download-csv"
        />
        <DownloadAnchor
          href={downloadUrl(run.id, 'bundle')}
          label="Download bundle"
          disabled={bundleDisabled}
          testId="download-bundle"
        />
      </div>

      {regenerate.isError ? (
        <p role="alert" className="text-sm text-red-700">
          {regenerate.error instanceof ApiError ? regenerate.error.detail : 'Failed to regenerate.'}
        </p>
      ) : null}
      {regenerateMessage !== null ? (
        <p role="status" className="text-sm text-green-700">
          {regenerateMessage}
        </p>
      ) : null}

      <div>
        <h2 className="text-lg font-semibold mb-2">Customers</h2>
        {run.customers.length === 0 ? (
          <p className="text-sm text-slate-600">No customer lines for this run yet.</p>
        ) : (
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="text-left border-b border-slate-300">
                <th className="py-2 pr-4">Customer</th>
                <th className="py-2 pr-4">Calls</th>
                <th className="py-2 pr-4">Minutes</th>
                <th className="py-2 pr-4">Cost</th>
                <th className="py-2 pr-4">PDF</th>
              </tr>
            </thead>
            <tbody>
              {run.customers.map((c) => (
                <tr
                  key={c.customerId}
                  className="border-b border-slate-200"
                  data-testid={`customer-row-${c.customerId}`}
                >
                  <td className="py-2 pr-4">{c.name}</td>
                  <td className="py-2 pr-4">{c.totalCalls}</td>
                  <td className="py-2 pr-4">{formatMinutes(c.totalSeconds)}</td>
                  <td className="py-2 pr-4">{formatPence(c.totalCostPence)}</td>
                  <td className="py-2 pr-4">
                    <DownloadAnchor
                      href={downloadUrl(run.id, { pdfCustomerId: c.customerId })}
                      label="Download PDF"
                      disabled={!c.pdfReady}
                      testId={`download-pdf-${c.customerId}`}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}
